import React, { useState } from "react";
import useSWR from "swr";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, AlertTriangle, FileText, Info, ArrowRight, Trash2 } from "lucide-react";

const fetcher = (u) => api.get(u).then(r=>r.data);

const ORDER = [
  { section: "Step 1 — Master data", keys: ["customers","suppliers","products","raw_materials","machines","moulds"] },
  { section: "Step 2 — Opening balances", keys: ["opening_stock_rm","opening_stock_fg"] },
  { section: "Step 3 — In-flight work", keys: ["sales_orders","production_history"] },
];

export default function DataImportPage() {
  const { user } = useAuth();
  const { data: entities } = useSWR("/import/entities", fetcher);
  const { data: voidSummary, mutate: refreshVoid } = useSWR(user?.role === "CEO_ADMIN" ? "/admin/void-records" : null, fetcher);
  const eMap = Object.fromEntries((entities||[]).map(e=>[e.key, e]));
  const [openKey, setOpenKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [purging, setPurging] = useState(false);

  const purgeVoid = async () => {
    const total = voidSummary?.total_purgeable || 0;
    if (total === 0) { toast.info("No purgeable VOID records found"); return; }
    const skipped = voidSummary?.total_referenced || 0;
    const msg = `Hard-delete ${total} VOID master record(s)?` +
      (skipped > 0 ? `\n\n${skipped} record(s) will be SKIPPED because they are still referenced by transactions.` : "") +
      `\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    setPurging(true);
    try {
      const r = await api.post("/admin/purge-void");
      toast.success(`Purged ${r.data.total_purged} record(s). Skipped ${r.data.total_skipped}.`);
      refreshVoid();
    } catch(e) { toast.error(e.response?.data?.detail || "Purge failed"); }
    finally { setPurging(false); }
  };

  const downloadTemplate = async (entity) => {
    const r = await api.get(`/import/template/${entity}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${entity}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadPreview = async (entity, f) => {
    if (!f) return;
    setBusy(true); setPreview(null); setResult(null);
    try {
      const form = new FormData(); form.append("file", f);
      const r = await api.post(`/import/preview/${entity}`, form, { headers: { "Content-Type":"multipart/form-data" }});
      setPreview({ entity, ...r.data });
      setFile(f);
    } catch(e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const commitImport = async () => {
    if (!file || !preview) return;
    if (preview.error_count > 0 && !window.confirm(`${preview.error_count} rows have errors and will be skipped. Continue with the other ${preview.ok_count} rows?`)) return;
    setBusy(true);
    try {
      const form = new FormData(); form.append("file", file);
      const r = await api.post(`/import/commit/${preview.entity}`, form, { headers: { "Content-Type":"multipart/form-data" }});
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} rows`);
    } catch(e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="import-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Data Import</h1>
        <p className="text-sm text-slate-500 max-w-3xl">Bring in your existing data from Zoho, Tally, or manual production books. Download the CSV template for the entity you want, fill it in Excel, upload, preview and commit.</p>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex gap-3">
        <Info size={20} className="text-sky-600 shrink-0 mt-0.5"/>
        <div className="text-sm text-slate-700">
          <div className="font-semibold text-slate-900 mb-1">Recommended import order</div>
          <ol className="list-decimal ml-5 space-y-0.5 text-[13px]">
            <li>Master data first — Customers, Suppliers, Products, Raw Materials, Machines, Moulds</li>
            <li>Opening balances — Raw material stock, FG stock (as on go-live date)</li>
            <li>In-flight sales orders & last few months' production history</li>
          </ol>
        </div>
      </div>

      {user?.role === "CEO_ADMIN" && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="void-purge-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trash2 size={16} className="text-red-600"/>
                <div className="font-semibold text-slate-900">Purge VOID master records</div>
              </div>
              <div className="text-xs text-slate-600 max-w-2xl">
                Hard-deletes legacy master records marked as VOID (from older soft-deletes). Records still referenced by any transaction (SO, PO, invoice, GRN, production order) are automatically skipped.
              </div>
              {voidSummary && (
                <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 font-mono" data-testid="void-purgeable-count">Purgeable: {voidSummary.total_purgeable}</span>
                  <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 font-mono" data-testid="void-referenced-count">Referenced (skipped): {voidSummary.total_referenced}</span>
                  {(voidSummary.collections||[]).map(c => (
                    <span key={c.collection} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono">{c.collection}: {c.void_total}</span>
                  ))}
                </div>
              )}
            </div>
            <button
              data-testid="btn-purge-void"
              onClick={purgeVoid}
              disabled={purging || !voidSummary || voidSummary.total_purgeable === 0}
              className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 whitespace-nowrap">
              {purging ? "Purging..." : "Purge VOID"}
            </button>
          </div>
        </div>
      )}

      {ORDER.map(sec => (
        <div key={sec.section}>
          <div className="text-xs uppercase font-semibold tracking-wider text-slate-500 mb-2">{sec.section}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {sec.keys.map(k => {
              const e = eMap[k]; if (!e) return null;
              const open = openKey === k;
              return (
                <div key={k} className={`bg-white border rounded-xl p-4 ${open?"border-sky-400 shadow":"border-slate-200"}`} data-testid={`import-card-${k}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText size={18} className="text-sky-600"/>
                      <div>
                        <div className="font-semibold text-slate-900">{e.label}</div>
                        <div className="text-[11px] font-mono text-slate-500">{k}</div>
                      </div>
                    </div>
                    <button data-testid={`btn-template-${k}`} onClick={()=>downloadTemplate(k)} className="text-xs px-2.5 py-1 rounded border border-slate-200 hover:border-sky-400 text-slate-700 flex items-center gap-1">
                      <Download size={12}/> Template
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-500 mb-2 line-clamp-2">
                    <span className="font-semibold text-slate-700">Zoho / source:</span> {e.zoho_hint}
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {e.headers.slice(0,6).map(h=><span key={h} className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{h}</span>)}
                    {e.headers.length>6 && <span className="text-[10px] text-slate-400">+{e.headers.length-6}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <label data-testid={`upload-label-${k}`} className="flex-1 flex items-center gap-2 text-xs px-3 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-700 cursor-pointer justify-center">
                      <Upload size={12}/> Upload CSV
                      <input data-testid={`upload-input-${k}`} type="file" accept=".csv" className="hidden" onChange={e=>{ setOpenKey(k); uploadPreview(k, e.target.files[0]); }}/>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {busy && <div className="text-slate-500 text-sm">Processing...</div>}

      {preview && (
        <div className="fixed inset-0 z-50 flex bg-black/40 items-center justify-center p-4" onClick={()=>{setPreview(null); setResult(null); setFile(null);}}>
          <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="import-preview-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between z-10">
              <div>
                <div className="text-xs text-slate-500">Preview import into</div>
                <h3 className="font-bold text-slate-900">{eMap[preview.entity]?.label}</h3>
              </div>
              <button onClick={()=>{setPreview(null); setResult(null); setFile(null);}} className="text-slate-400 hover:text-slate-900" data-testid="btn-close-preview">✕</button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500 font-semibold">Total Rows</div><div className="font-mono text-lg font-bold">{preview.total_rows}</div></div>
                <div className="bg-emerald-50 rounded-lg p-3"><div className="text-[10px] uppercase text-emerald-700 font-semibold">Ready to Import</div><div className="font-mono text-lg font-bold text-emerald-700" data-testid="preview-ok-count">{preview.ok_count}</div></div>
                <div className="bg-red-50 rounded-lg p-3"><div className="text-[10px] uppercase text-red-700 font-semibold">Rows with Errors</div><div className="font-mono text-lg font-bold text-red-700" data-testid="preview-error-count">{preview.error_count}</div></div>
              </div>

              {preview.warnings?.length>0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                  <div className="font-semibold flex items-center gap-1"><AlertTriangle size={14}/> Warnings</div>
                  {preview.warnings.map((w)=><div key={w}>· {w}</div>)}
                </div>
              )}

              <div>
                <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Sample rows</div>
                <div className="overflow-auto border border-slate-200 rounded-lg">
                  <table className="data-table" data-testid="preview-sample-table">
                    <thead><tr>{(preview.sample[0] ? Object.keys(preview.sample[0]) : []).map(h=><th key={h}>{h}</th>)}</tr></thead>
                    <tbody>{preview.sample.map((r,i)=><tr key={`sample-${i}`}>{Object.entries(r).map(([k,v])=><td key={k} className="text-xs font-mono">{String(v)}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              </div>

              {preview.errors?.length>0 && (
                <div>
                  <div className="text-xs font-semibold uppercase text-red-700 mb-2 flex items-center gap-1"><AlertTriangle size={14}/> Rows that will be skipped</div>
                  <div className="border border-red-200 rounded-lg overflow-auto max-h-56">
                    <table className="data-table">
                      <thead><tr><th>Row</th><th>Errors</th></tr></thead>
                      <tbody>{preview.errors.map((e)=><tr key={e.row}><td className="font-mono text-xs">{e.row}</td><td className="text-xs text-red-700">{e.errors.join(", ")}</td></tr>)}</tbody>
                    </table>
                  </div>
                </div>
              )}

              {result ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="import-result">
                  <div className="flex items-center gap-2 text-emerald-900 font-semibold mb-1"><CheckCircle2 size={18}/> Import complete</div>
                  <div className="text-sm text-slate-700">Inserted <span className="font-bold font-mono">{result.inserted}</span> rows. Skipped {result.skipped_count}.</div>
                  {result.skipped?.length>0 && <div className="text-[11px] text-slate-500 mt-2 max-h-24 overflow-auto">{result.skipped.map((s)=><div key={`skip-${s.row}`}>row {s.row}: {s.reason}</div>)}</div>}
                </div>
              ) : (
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                  <button onClick={()=>{setPreview(null); setFile(null);}} className="text-sm px-3 py-1.5 rounded border border-slate-200">Cancel</button>
                  <button data-testid="btn-commit-import" onClick={commitImport} disabled={busy || preview.ok_count===0} className="text-sm px-4 py-1.5 rounded bg-sky-600 text-white flex items-center gap-1 disabled:opacity-50">
                    Import {preview.ok_count} rows <ArrowRight size={14}/>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
